import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import { FormattedMessage, useIntl } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { POPUP_STATUS, VAR_TYPES, MODAL_CONTENT } from "constants/constants";
import { useState, useEffect } from "react";
import {
  onAdd,
  onChange,
  onDelete,
  renderCommonBlock,
  renderStartDateEndDate,
} from "utils/tableContentHelper";
import { phoneSize } from "utils/screenSizes";
import AlertPopup from "components/alert-popup/AlertPopup";
import ModalFooter from "components/modal/ModalFooter";
import PropTypes from "prop-types";
import { ERROR_DATE_TIMING, ERROR_MISSING_FIELDS } from "constants/popup-codes";

const ManageExternalTraining = ({
  onClose,
  onValidate,
  externalTraining,
  usersByIds,
  getUpdatedValues,
  lang,
  setElementsToDelete,
}) => {
  const intl = useIntl();
  const [data, setData] = useState(externalTraining);
  const [initialData, setInitialData] = useState(externalTraining);
  const [status, setStatus] = useState();
  const [code, setCode] = useState();
  const [reloadData, setReloadData] = useState(false);

  const dropdownOptions = ({ column }) => {
    switch (column) {
      case "funding_type":
        return [
          { label: "PLAN", value: "PLAN" },
          { label: "CPF", value: "CPF" },
        ];
      case "mandatory":
        return [
          {
            label: intl.formatMessage({
              id: "yes",
              defaultMessage: "Oui",
            }),
            value: true,
          },
          {
            label: intl.formatMessage({
              id: "no",
              defaultMessage: "Non",
            }),
            value: false,
          },
        ];
      case "provides_certificate":
        return [
          { label: "Certification", value: "Certification" },
          { label: "Habilitation", value: "Habilitation" },
          { label: "RNCP", value: "RNCP" },
          { label: "Non", value: "Non" },
        ];
      default:
        break;
    }
  };

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
    setReloadData(!reloadData);
  };

  const onClickValidate = () => {
    let errorMessage;
    data?.some((e) => {
      if (
        !e.title ||
        e.provides_certificate == null || e.provides_certificate === "" ||
        ![true, false, 0, 1].includes(e.mandatory) ||
        !e.duration ||
        !e.start_date ||
        !e.end_date ||
        !e.funding_type
      )
        errorMessage = ERROR_MISSING_FIELDS;
      else if (e.end_date < e.start_date) errorMessage = ERROR_DATE_TIMING;
    });
    errorMessage ? setError(errorMessage) : onValidate();
  };

  useEffect(() => {
    if (
      initialData?.length < externalTraining?.length &&
      externalTraining?.length > 0
    ) {
      setData(externalTraining);
      setInitialData(externalTraining);
    }
  }, [initialData, externalTraining]);

  const header = [
    {
      label: {
        key: "title",
        value: (
          <FormattedMessage id="training_label" defaultMessage="Formation" />
        ),
      },
      icon: "book",
      width: "fit-content",
    },
    {
      label: {
        key: "provides_certificate",
        value: (
          <FormattedMessage
            id="certificate_label"
            defaultMessage="Certificat"
          />
        ),
      },
      icon: "book",
      width: "fit-content",
    },
    {
      label: {
        key: "mandatory",
        value: (
          <FormattedMessage id="required_label" defaultMessage="Obligatoire" />
        ),
      },
      icon: "check-list",
      width: "fit-content",
    },
    {
      label: {
        key: "duration",
        value: <FormattedMessage id="duration_label" defaultMessage="Durée" />,
      },
      icon: "time",
      width: "fit-content",
    },
    {
      label: {
        key: "start_date",
        value: <FormattedMessage id="start" defaultMessage="Début" />,
      },
      icon: "calendar",
      width: "fit-content",
    },
    {
      label: {
        key: "end_date",
        value: <FormattedMessage id="end" defaultMessage="Fin" />,
      },
      icon: "calendar",
      width: "fit-content",
    },
    {
      label: {
        key: "funding_type",
        value: (
          <FormattedMessage id="funding_label" defaultMessage="Financement" />
        ),
      },
      icon: "salary",
      width: "fit-content",
    },

    {
      label: {
        key: "updated_by",
        value: (
          <FormattedMessage id="updated_by" defaultMessage="Modifié par" />
        ),
      },
      icon: "user",
      width: "fit-content",
    },
    {
      label: {
        key: "updated_at",
        value: <FormattedMessage id="updated_at" defaultMessage="le" />,
      },
      icon: "calendar",
      width: "fit-content",
    },
  ];

  const formatContent = (content) => {
    const certificateOptions = dropdownOptions({ column: "provides_certificate" });
    const mandatoryOptions = dropdownOptions({ column: "mandatory" });
    const fundingOptions = dropdownOptions({ column: "funding_type" });

    return content?.length > 0
      ? content?.map((elm, index) => ({
          title: (
            <InputText
              value={elm?.title}
              type={VAR_TYPES.STRING}
              onChange={(val) =>
                onChange({
                  getUpdatedValues,
                  id: elm?.id,
                  key: "title",
                  setData,
                  data,
                  val: val.target.value,
                })
              }
              dataTestId={`${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-title-${index}`}
            />
          ),
          provides_certificate: (
            <InputSelect
              id="provides_certificate"
              value={elm?.provides_certificate}
              options={
                elm?.provides_certificate && !certificateOptions.some(o => o.value === elm.provides_certificate)
                  ? [...certificateOptions, { label: String(elm.provides_certificate), value: elm.provides_certificate }]
                  : certificateOptions
              }
              name="provides_certificate"
              setValue={(name, val) =>
                onChange({
                  getUpdatedValues,
                  id: elm?.id,
                  key: name,
                  setData,
                  data,
                  val,
                })
              }
              dataTestId={`${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-provides_certificate-${index}`}
            />
          ),
          mandatory: (
            <InputSelect
              id="mandatory"
              value={elm?.mandatory}
              options={mandatoryOptions}
              name="mandatory"
              setValue={(name, val) =>
                onChange({
                  getUpdatedValues,
                  id: elm?.id,
                  key: name,
                  setData,
                  data,
                  val,
                })
              }
              dataTestId={`${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-mandatory-${index}`}
            />
          ),
          duration: (
            <InputText
              value={elm?.duration}
              type={VAR_TYPES.NUMBER}
              onChange={(val) =>
                onChange({
                  getUpdatedValues,
                  id: elm?.id,
                  key: "duration",
                  setData,
                  data,
                  val: Number(val.target.value),
                })
              }
              dataTestId={`${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-duration-${index}`}
            />
          ),
          ...renderStartDateEndDate({
            data,
            getUpdatedValues,
            start_date: elm?.start_date,
            end_date: elm?.end_date,
            setData,
            id: elm?.id,
            onChangeDates: onChange,
            lang,
            modalType: MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING,
            index,
          }),
          funding_type: (
            <InputSelect
              id="funding_type"
              value={elm?.funding_type}
              options={fundingOptions}
              name="funding_type"
              setValue={(name, val) =>
                onChange({
                  getUpdatedValues,
                  id: elm?.id,
                  key: name,
                  setData,
                  data,
                  val,
                })
              }
              dataTestId={`${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-funding_type-${index}`}
            />
          ),
          ...renderCommonBlock({
            data,
            getUpdatedValues,
            id: elm.id,
            onDelete,
            setData,
            updated_at: elm.updated_at,
            updated_by: usersByIds.find((user) => user.id === elm.updated_by)
              ?.name,
            lang,
            setElementsToDelete,
            deleteBtnTestId: `${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-delete_btn-${index}`,
          }),
        }))
      : [];
  };

  return (
    <>
      <button
        className="button-style"
        onClick={() => onAdd({ header, data, setData })}
        data-testid={`${MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}_modal-add_training_btn`}
      >
        <Typography
          className="link-1 violet"
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_row_training"
                defaultMessage="Ajouter une formation"
              />
            </>
          }
        />
      </button>
      <br />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="salary"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={() => {
          onClickValidate();
          setReloadData(!reloadData);
        }}
        modalType={MODAL_CONTENT.MANAGE_EXTERNAL_TRAINING}
      />
    </>
  );
};

export default ManageExternalTraining;

ManageExternalTraining.propTypes = {
  onClose: PropTypes.func,
  onValidate: PropTypes.func,
  externalTraining: PropTypes.array,
  usersByIds: PropTypes.array,
  getUpdatedValues: PropTypes.func,
  lang: PropTypes.string,
  setElementsToDelete: PropTypes.func,
};
